import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, TextField, Typography, styled } from '@mui/material';

import { TagTypeValue, isBasicTag, isContentTag, isPassportTag } from '../constants';
import type { ExperienceOfferItem } from '../hooks';
import { useTagAssignmentsForForm } from '../hooks/useTagAssignmentsForForm';
import { SectionStyled as BaseSectionStyled, SectionContentStyled, SectionHeaderStyled } from '../styles';

import TagAssignmentsDestination from './TagAssignmentsDestination/TagAssignmentsDestination';
import TagAssignmentsExperience from './TagAssignmentsExperience/TagAssignmentsExperience';
import TagAssignmentsHotel from './TagAssignmentsHotel/TagAssignmentsHotel';

export type EntityType = 'HOTEL' | 'EXPERIENCE' | 'DESTINATION';

export interface AssignmentFormData {
  entityType: EntityType;
  entityId: string;
}

const SectionStyled = styled(BaseSectionStyled)(({ theme }) => ({
  '&:not(:last-child)': {
    marginBottom: theme.spacing(4),
  },
}));

interface Props {
  tagId?: string;
  assignments: Array<AssignmentFormData>;
  tagType: TagTypeValue;
  isSaving?: boolean;
  onAssignmentsChange: (assignments: Array<AssignmentFormData>) => void;
}

export default function TagAssignments({ tagId, assignments, tagType, isSaving = false, onAssignmentsChange }: Props) {
  const [primaryExperienceOffer, setPrimaryExperienceOffer] = useState<ExperienceOfferItem>();
  const [hotelEntityIdFilter, setHotelEntityIdFilter] = useState('');
  const tagAssignmentsData = useTagAssignmentsForForm({
    tagId,
  });
  const savedAssignments = tagAssignmentsData.assignments;
  const savedAssignmentsPersisted = tagAssignmentsData.savedAssignments;
  const passportTag = isPassportTag(tagType);
  const contentTag = isContentTag(tagType);
  const basicTag = isBasicTag(tagType);
  const handlePrimaryExperienceOfferChange = useCallback((offer?: ExperienceOfferItem) => {
    setPrimaryExperienceOffer(offer);
  }, []);

  useEffect(() => {
    if (tagId && savedAssignments.length > 0 && assignments.length === 0) {
      onAssignmentsChange(savedAssignments);
    }
  }, [tagId, savedAssignments, assignments.length, onAssignmentsChange]);

  const assignmentsByType = useMemo(() => {
    const grouped = {
      HOTEL: assignments.filter((a) => a.entityType === 'HOTEL').map((a) => a.entityId),
      EXPERIENCE: assignments.filter((a) => a.entityType === 'EXPERIENCE').map((a) => a.entityId),
      DESTINATION: assignments.filter((a) => a.entityType === 'DESTINATION').map((a) => a.entityId),
    };
    return grouped;
  }, [assignments]);

  const handleHotelChange = (selectedOfferIds: Array<string>) => {
    const otherAssignments = assignments.filter((a) => a.entityType !== 'HOTEL');
    const hotelAssignments: Array<AssignmentFormData> = selectedOfferIds.map((offerId) => ({
      entityType: 'HOTEL',
      entityId: offerId,
    }));
    onAssignmentsChange([...otherAssignments, ...hotelAssignments]);
  };

  const handleExperienceChange = (selectedOfferIds: Array<string>) => {
    const otherAssignments = assignments.filter((a) => a.entityType !== 'EXPERIENCE');
    const experienceAssignments: Array<AssignmentFormData> = selectedOfferIds.map((offerId) => ({
      entityType: 'EXPERIENCE',
      entityId: offerId,
    }));
    onAssignmentsChange([...otherAssignments, ...experienceAssignments]);
  };

  const handleDestinationChange = (selectedPlaceIds: Array<string>) => {
    const savedDestinationIds = tagId
      ? savedAssignmentsPersisted.filter((a) => a.entityType === 'DESTINATION').map((a) => a.entityId)
      : [];

    const allDestinationIds = [
      ...savedDestinationIds,
      ...selectedPlaceIds.filter((id) => !savedDestinationIds.includes(id)),
    ];

    const otherAssignments = assignments.filter((a) => a.entityType !== 'DESTINATION');
    const destinationAssignments: Array<AssignmentFormData> = allDestinationIds.map((placeId) => ({
      entityType: 'DESTINATION',
      entityId: placeId,
    }));
    onAssignmentsChange([...otherAssignments, ...destinationAssignments]);
  };

  return (
    <Box>
      <SectionStyled>
        <SectionHeaderStyled>
          <Typography component="h2" variant="h4" fontWeight="bold">
            Tagged experiences
          </Typography>
          {assignmentsByType.EXPERIENCE.length > 0 && (
            <Typography variant="body2" ml={2} color="text.secondary">
              ({assignmentsByType.EXPERIENCE.length} selected)
            </Typography>
          )}
        </SectionHeaderStyled>
        <SectionContentStyled>
          <TagAssignmentsExperience
            tagId={tagId}
            tagType={tagType}
            voucherBasedOnly={passportTag}
            selectedOfferIds={assignmentsByType.EXPERIENCE}
            onSelectionChange={handleExperienceChange}
            savedOfferIds={savedAssignmentsPersisted
              .filter((a) => a.entityType === 'EXPERIENCE')
              .map((a) => a.entityId)}
            onPrimaryOfferChange={passportTag ? handlePrimaryExperienceOfferChange : undefined}
          />
        </SectionContentStyled>
      </SectionStyled>
      {!passportTag && (
        <SectionStyled>
          <SectionHeaderStyled>
            <Typography component="h2" variant="h4" fontWeight="bold">
              Tagged destinations
            </Typography>
            {assignmentsByType.DESTINATION.length > 0 && (
              <Typography variant="body2" ml={2} color="text.secondary">
                ({assignmentsByType.DESTINATION.length} selected)
              </Typography>
            )}
          </SectionHeaderStyled>
          <SectionContentStyled>
            <TagAssignmentsDestination
              tagId={tagId}
              selectedPlaceIds={assignmentsByType.DESTINATION}
              onSelectionChange={handleDestinationChange}
            />
          </SectionContentStyled>
        </SectionStyled>
      )}
      {!contentTag && (
        <SectionStyled
          aria-disabled={passportTag && assignmentsByType.EXPERIENCE.length === 0}
          sx={
            passportTag && assignmentsByType.EXPERIENCE.length === 0
              ? {
                  bgcolor: 'action.disabledBackground',
                  opacity: 0.65,
                  pointerEvents: 'none',
                }
              : undefined
          }
        >
          <SectionHeaderStyled
            sx={{
              alignItems: { xs: 'stretch', md: 'center' },
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 1,
            }}
          >
            <Box display="flex" alignItems="center" flexShrink={0}>
              <Typography component="h2" variant="h4" fontWeight="bold">
                Tagged hotels
              </Typography>
              {assignmentsByType.HOTEL.length > 0 && (
                <Typography variant="body2" ml={2} color="text.secondary">
                  ({assignmentsByType.HOTEL.length} selected)
                </Typography>
              )}
            </Box>
            <TextField
              label="Filter by entity ID"
              onChange={(event) => setHotelEntityIdFilter(event.target.value)}
              placeholder="Paste or type an entity ID"
              size="small"
              sx={{ flex: '1 1 0', minWidth: 0, width: { xs: '100%', md: 'auto' } }}
              value={hotelEntityIdFilter}
            />
          </SectionHeaderStyled>
          <SectionContentStyled>
            <TagAssignmentsHotel
              tagId={tagId}
              entityIdFilter={hotelEntityIdFilter}
              selectedOfferIds={assignmentsByType.HOTEL}
              onSelectionChange={handleHotelChange}
              savedOfferIds={savedAssignmentsPersisted.filter((a) => a.entityType === 'HOTEL').map((a) => a.entityId)}
              isPassportTag={passportTag}
              selectionMode={basicTag ? 'direct-id' : 'search'}
              isSaving={isSaving}
              disabled={passportTag && assignmentsByType.EXPERIENCE.length === 0}
              experienceOfferId={assignmentsByType.EXPERIENCE[assignmentsByType.EXPERIENCE.length - 1]}
              experienceOffer={
                primaryExperienceOffer?.id === assignmentsByType.EXPERIENCE[assignmentsByType.EXPERIENCE.length - 1]
                  ? primaryExperienceOffer
                  : undefined
              }
            />
          </SectionContentStyled>
        </SectionStyled>
      )}
    </Box>
  );
}
